import * as z from 'zod';

/**
 * Every warning the public API can return, keyed by a stable code.
 *
 * Warnings do not block a request, so a client that wants to react to one has to be able
 * to identify it. `detail` is prose and several messages interpolate runtime values, so
 * the code — surfaced as `meta.warningCode` on the response — is the thing to branch on.
 *
 * The catalog is also what generates the warning reference in the API documentation, so a
 * warning added here shows up for customers without a second edit.
 */
export type ApiWarningDefinition = {
  /** Stable identifier, safe to branch on. Never reused for a different condition. */
  code: string;
  /** When the warning fires, in terms a customer can act on. */
  summary: string;
  /**
   * The shape of `detail`. Slots in {braces} are filled at request time; a definition
   * with no slots is emitted verbatim.
   */
  messageTemplate: string;
  /**
   * Request field the warning points at, as it appears in `source.pointer` after the
   * `data/attributes/` prefix. Absent when the warning applies to the whole loan.
   */
  field?: string;
};

const define = <T extends Record<string, Omit<ApiWarningDefinition, 'code'>>>(
  definitions: T,
): { [K in keyof T]: ApiWarningDefinition & { code: K } } =>
  Object.fromEntries(Object.entries(definitions).map(([code, definition]) => [code, { ...definition, code }])) as never;

export const LOAN_API_WARNINGS = define({
  FIRST_COLLECTED_PAYMENT_DATE_IN_PAST: {
    summary: 'The first collected payment date has already passed, so the loan boards past due.',
    messageTemplate: 'Because `firstCollectedPaymentDate` is in the past, the loan will be marked as past due.',
    field: 'firstCollectedPaymentDate',
  },
  STOP_EMAILS_FLAGS_LOAN: {
    summary: 'Suppressing borrower email also flags the loan for review.',
    messageTemplate: 'Because `stopEmails` is set to true, the loan will be automatically flagged.',
    field: 'stopEmails',
  },
  STOP_PAYMENTS_FLAGS_LOAN: {
    summary: 'Suppressing borrower payments also flags the loan for review.',
    messageTemplate: 'Because `stopPayments` is set to true, the loan will be automatically flagged.',
    field: 'stopPayments',
  },
  MISSING_BEGINNING_OF_YEAR_PRINCIPAL_BALANCE: {
    summary:
      'The loan was already being serviced before the boarding year, so year-end tax reporting needs its opening balance.',
    messageTemplate:
      'Loan that has been active since before start of boarding year should include `beginningOfYearPrincipalBalance` for tax reporting.',
  },
  MISSING_YTD_INTEREST_PAID: {
    summary:
      'The loan was already being serviced before the boarding year, so year-end tax reporting needs interest paid to date.',
    messageTemplate:
      'Loan that has been active since before start of boarding year should include `ytdInterestPaid` for tax reporting.',
  },
  INSURANCE_POLICY_INCOMPLETE: {
    summary: 'An insurance policy was partially supplied and will be dropped rather than boarded incomplete.',
    messageTemplate:
      'Insurance policy {n} is missing {a field|fields} and will not be added to the loan: {missingFields}',
  },
  ESCROW_COMPANY_MISSING_POLICY_NUMBER: {
    summary: 'An escrow company was supplied without its policy number and will be dropped.',
    messageTemplate: 'Escrow company {n} is missing `escrow{n}PolicyNumber` and will not be added to the loan',
  },
  CORRESPONDENT_LENDER_PHONE_WITHOUT_LENDER: {
    summary: 'Correspondent contact details were supplied without the correspondent lender itself.',
    messageTemplate: 'correspondentLenderPhone must be defined to board loan with correspondent data',
    field: 'correspondentLenderPhone',
  },
  CORRESPONDENT_LENDER_EMAIL_WITHOUT_LENDER: {
    summary: 'Correspondent contact details were supplied without the correspondent lender itself.',
    messageTemplate: 'correspondentLenderEmail must be defined to board loan with correspondent data',
    field: 'correspondentLenderEmail',
  },
  ESCROWED_LOAN_MISSING_MORTGAGE_INSURANCE: {
    summary: 'An escrowed loan supplied no mortgage insurance amount, so it boards at zero.',
    messageTemplate:
      'Escrowed loan should include `miAmount` or `monthlyPaymentMi` (deprecated) (mortgage insurance). It is currently missing, so it will default to 0.',
  },
  GNMA_INVESTOR_LOAN_ID_NOT_NUMERIC: {
    summary: 'A Ginnie Mae loan carries a Unique Loan ID that GNMA RFS reporting cannot accept.',
    messageTemplate:
      'For Ginnie Mae loans, `investorLoanId` should be the numeric Ginnie Mae Unique Loan ID (up to 9 digits). The current value cannot be reported in GNMA RFS reporting.',
    field: 'investorLoanId',
  },
  GNMA_PRODUCT_TYPE_MISSING: {
    summary: 'A Ginnie Mae loan has no product type, so it cannot be included in GNMA RFS reporting.',
    messageTemplate:
      'Ginnie Mae loans should include `productType` (fha, va, usda, or pih). Without it, the loan cannot be included in GNMA RFS reporting.',
    field: 'productType',
  },
  GNMA_PRODUCT_TYPE_NOT_ELIGIBLE: {
    summary: 'A Ginnie Mae loan has a product type with no GNMA equivalent.',
    messageTemplate:
      'Product type `{productType}` does not map to a Ginnie Mae loan type (fha, va, usda, or pih), so the loan cannot be included in GNMA RFS reporting.',
    field: 'productType',
  },
  INTEREST_TYPE_WITHOUT_INTEREST_ONLY: {
    summary: 'An interest type was supplied on a loan that is not interest only, and is ignored.',
    messageTemplate:
      'Interest type is only supported for interest only loans. This field will be ignored and the loan will use the conventional 30-360 interest calculator.',
    field: 'interestType',
  },
  APPRAISAL_TYPE_DEFAULTED: {
    summary: 'A property appraisal was supplied without a type, so it boards as "Appraisal".',
    messageTemplate: 'Appraisal type will default to "Appraisal" if left blank',
    field: 'addressAppraisal.type',
  },
  APPRAISAL_INCOMPLETE: {
    summary: 'A property appraisal is missing a required field. The pointer names the appraisal and the field.',
    messageTemplate: 'Appraisal {field} is required when adding a property appraisal',
    field: 'addressAppraisal.amount',
  },
  FICO_SCORE_OUT_OF_RANGE: {
    summary:
      'A borrower FICO score falls outside the valid range and will not be stored. The pointer names which borrower.',
    messageTemplate: 'Fico score must be between 300 - 850',
    field: 'primaryBorrowerDetails.ficoScore',
  },
  BUILDER_STATEMENTS_NOT_SUPPORTED: {
    summary: 'Builder statements were requested on a loan that is not a construction loan.',
    messageTemplate: 'Loan is not valid for sending builder statements',
    field: 'builderIsResponsibleForPayment',
  },
  BUILDER_MISSING_COMPANY_NAME: {
    summary: 'Builder details were supplied without a company name, so no builder is created.',
    messageTemplate: 'Builder must have company name to be created',
    field: 'builderCompanyName',
  },
  BUILDER_ADDRESS_INCOMPLETE: {
    summary: 'A builder mailing address was partially supplied.',
    messageTemplate: 'Builder mailing address is missing required {field|fields} {missingFields}.',
    field: 'builderAddressLine1',
  },
  INTEREST_ONLY_FLAG_REQUIRED: {
    summary: 'Interest-only terms were supplied without setting the interest-only flag.',
    messageTemplate: 'Interest only flag is required for interest only loans.',
    field: 'interestOnlyTermMonths',
  },
});

export type LoanApiWarningCode = keyof typeof LOAN_API_WARNINGS;

export const LOAN_API_WARNING_LIST: ApiWarningDefinition[] = Object.values(LOAN_API_WARNINGS);

export type LoanWarn = (code: LoanApiWarningCode, options?: { message?: string; path?: PropertyKey[] }) => void;

/**
 * Turns a rule that can only raise catalogued warnings into a Zod refinement.
 *
 * The rule never receives the `RefinementCtx`, so there is no way for it to add an issue
 * that has no entry above — which is what keeps the published warning reference complete
 * as warnings are added. The path defaults to the entry's `field`, so that stays honest too.
 */
export const loanWarningRule =
  <T>(rule: (data: T, warn: LoanWarn) => void) =>
  (data: T, ctx: z.RefinementCtx<T>) => {
    rule(data, (code, options) => {
      const definition = LOAN_API_WARNINGS[code];
      const path = options?.path ?? (definition.field ? [definition.field] : undefined);
      ctx.addIssue({
        code: 'custom',
        message: options?.message ?? definition.messageTemplate,
        params: { warningCode: code },
        ...(path ? { path } : {}),
      });
    });
  };
